import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes, ReactNode } from 'react';
import { Icon } from '../../../lib/icon';
import { cn } from '../../../lib/utils';

const notificationBannerVariants = cva('relative w-full rounded-[var(--radius-card)] border p-5', {
  variants: {
    variant: {
      error: 'border-border-subtle bg-error-subtle text-error-on-subtle',
      success: 'border-border-subtle bg-success-subtle text-success-on-subtle',
      warning: 'border-border-subtle bg-warning-subtle text-warning-on-subtle',
      info: 'border-input-border bg-card text-foreground [&_p]:text-muted-foreground',
      neutral: 'border-transparent bg-muted text-foreground',
    },
  },
  defaultVariants: {
    variant: 'info',
  },
});

interface NotificationBannerProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof notificationBannerVariants> {
  icon?: ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
}

function NotificationBanner({ className, variant, icon, onDismiss, dismissLabel, children, ...props }: NotificationBannerProps) {
  return (
    <div role="alert" className={cn(notificationBannerVariants({ variant }), className)} {...props}>
      <div className="flex items-start gap-3">
        <div className="shrink-0" hidden={icon == null}>
          {icon}
        </div>
        <div className={cn('flex flex-1 flex-col gap-2', { 'pr-8': !!onDismiss })}>{children}</div>
      </div>
      {onDismiss != null && (
        <button
          type="button"
          onClick={onDismiss}
          className="absolute top-4 right-4 size-8 rounded-full bg-muted p-1.5 text-muted-foreground transition-colors hover:bg-muted/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={dismissLabel}
        >
          <Icon name="close" size="md" className="text-icons-main" />
        </button>
      )}
    </div>
  );
}

function NotificationBannerTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn('font-semibold text-heading-sm leading-heading-sm', className)} {...props} />;
}

function NotificationBannerDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-base leading-[--leading-body]', className)} {...props} />;
}

function NotificationBannerAction({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex shrink-0 items-center', className)} {...props} />;
}

export { NotificationBanner, NotificationBannerAction, NotificationBannerDescription, NotificationBannerTitle, notificationBannerVariants };
